import { generatorOptions, planRandomGenerator, type GeneratorInputs, type GeneratorFamily, type GeneratorCommand, type GeneratorOptions } from '../janus-engine/inventory/random-generator';
import { decodeHtmlEntitiesForPlainText } from './decode-html-entities';
import { browserRollSource } from '../janus-engine/actions/roll-source';
import { loadSpawnEncounter } from './spawn-browser-bridge';
import { refuseUnported } from '../janus-engine/unported';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { generatorDestination } from '../janus-engine/inventory/generator-destination';
import { prepareGeneratorDelivery, GENERATOR_SAVE_PATH } from './generator-delivery';
import { truthFetch } from './truth-sink';

export { generatorDestination };

type Row = Record<string, unknown>;
const packages = new Map<string, Promise<GeneratorInputs>>();
const value = (id: string): unknown => {
    const element = document.getElementById(id) as HTMLInputElement | null;
    return element?.type === 'checkbox' ? element.checked ? element.value : 0 : element?.value ?? '';
};
export function loadRandomGeneratorInputs(destination = generatorDestination(), optionsOnly = false): Promise<GeneratorInputs> {
    const key = `${destination}:${optionsOnly ? 'options' : 'outcome'}`;
    const pending = packages.get(key); if (pending) return pending;
    const loading = (async () => {
        const response = await fetch(BASE_URL + 'options/api/random_generator_inputs_inc.php?destination_id=' + encodeURIComponent(destination)
            + (optionsOnly ? '&view=options' : ''), { credentials: 'same-origin' });
        const data = await response.json() as GeneratorInputs;
        if (!response.ok || data.schema !== 'random-generator-inputs.v1' || !data.base?.catalog) throw new Error('generator:inputs-unavailable');
        return data;
    })();
    packages.set(key, loading);
    loading.catch(() => { if (packages.get(key) === loading) packages.delete(key); });
    return loading;
}
export async function randomGeneratorOptions() { return generatorOptions(await loadRandomGeneratorInputs(generatorDestination(), true)); }

const pendingCommands = new Map<string, { command: GeneratorCommand; fingerprint: string; deliver: ReturnType<typeof prepareGeneratorDelivery> }>();
const running = new Set<string>();
interface GeneratorSaved { ok?: boolean; error?: string; summary?: string; created_ids?: string[] }
async function saveGenerator(family: GeneratorFamily, values: Row, destination: string, key: string): Promise<{ command: GeneratorCommand; result: GeneratorSaved }> {
    const fingerprint = JSON.stringify(values), cached = pendingCommands.get(key);
    let prepared = cached?.fingerprint === fingerprint ? cached : null;
    if (!prepared) {
        const [inputs, encounter] = await Promise.all([loadRandomGeneratorInputs(destination), loadSpawnEncounter(destination)]);
        const roll = browserRollSource();
        const command = planRandomGenerator(family, values, inputs, encounter, destination,
            { uuid: () => crypto.randomUUID().toUpperCase(), die: sides => roll.d(sides) });
        prepared = { command, fingerprint, deliver: prepareGeneratorDelivery(command, inputs, encounter) };
        pendingCommands.set(key, prepared);
    }
    const { command } = prepared;
    const response = await truthFetch(BASE_URL + GENERATOR_SAVE_PATH, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command) });
    const result = await response.json() as GeneratorSaved;
    if (!response.ok || !result.ok) throw new Error(result.error ?? `generator:save-http:${response.status}`);
    await prepared.deliver(response);
    pendingCommands.delete(key);
    window.dispatchEvent(new CustomEvent('ft:random-generator-saved', { detail: { destination, ids: result.created_ids, family } }));
    return { command, result };
}

/** One bridge owner holds retry identities for both the early cards and post-template Add Object. */
export async function placeManualDigsite(values: Row, destination: string): Promise<string | null> {
    // The shared definition reader can classify an ordinary pick without the loot package.
    const readDefinition = (window as unknown as { fetchAndCacheObject: (id: string) => Promise<Row | null> }).fetchAndCacheObject;
    const definition = await readDefinition(String(values.inventory_item_id ?? ''));
    if (!definition) throw new Error('Item details are unavailable. Please retry.');
    if (readItemField(definition, 'item_type') !== 'digsite') return null;
    const key = `${destination}:manual_digsite`;
    if (running.has(key)) throw new Error('Digsite placement is still saving.');
    running.add(key);
    try {
        const { command } = await saveGenerator('manual_digsite', values, destination, key);
        const root = command.rows.find(op => op.kind === 'inventory' && readInventoryField({ inventory: op.row }, 'possess_id') === destination);
        if (!root) throw new Error('generator:missing-site-outcome');
        return String(readInventoryField({ inventory: root.row }, 'inventory_id'));
    } finally { running.delete(key); }
}
/** Existing inline controls supply element ids, just like queryDataInputs. No legacy rule POST. */
export async function generateRandomInventory(family: GeneratorFamily, warningId: string, inputs: Record<string, string>): Promise<void> {
    const values: Row = Object.fromEntries(Object.entries(inputs).map(([key, id]) => [key, value(id)]));
    const destination = generatorDestination(document, inputs);
    const key = `${destination}:${String(values.random_generator_container_inventory_id ?? '')}:${family}`;
    if (running.has(key)) return;
    running.add(key);
    const warning = document.getElementById(warningId);
    try {
        if (!destination) throw new Error('generator:destination-required');
        const { command, result } = await saveGenerator(family, values, destination, key);
        if (warning) warning.textContent = result.summary || command.summary;
        // A saved outcome stays successful if the following paint is interrupted. Retrying
        // a transport failure above reuses its operation; a failed refresh must never redraw it.
        try {
            await (window as Window & { ftRefreshEncounterXZAfterInventoryChange?: () => Promise<unknown> }).ftRefreshEncounterXZAfterInventoryChange?.();
            const container = String(values.random_generator_container_inventory_id ?? '');
            if (container) {
                const open = (window as unknown as { openItemInfo?: (id: string, refresh?: boolean) => Promise<unknown> }).openItemInfo;
                await open?.(container, false);
            }
        } catch {
            if (warning) warning.textContent = `${result.summary || command.summary} Refresh the inventory to see the saved result.`;
        }
    } catch (error) {
        if (warning) warning.textContent = `Generation failed — retry: ${error instanceof Error ? error.message : String(error)}`;
        const detail = error instanceof Error ? error.message : 'planning-failed';
        refuseUnported(/^(?:generator:|spawn:|from-base-|unported:)/.test(detail) ? detail : `generator:${detail}`, undefined, detail);
    } finally { running.delete(key); }
}

declare global { interface Window { ftGenerateRandomInventory: typeof generateRandomInventory; ftPlaceManualDigsite: typeof placeManualDigsite } }
window.ftGenerateRandomInventory = generateRandomInventory;
window.ftPlaceManualDigsite = placeManualDigsite;
/** Container cards and encounter panels use the same option painter and raw package. */
export function populateRandomGeneratorOptions(root: Element | Document, options: GeneratorOptions): void {
    root.querySelectorAll<HTMLSelectElement>('select[data-ft-loot-pool]').forEach(select => {
        if (select.dataset.ftLootLoaded === '1') return;
        const iconic = select.dataset.ftLootPool === 'iconic_book';
        const pool = iconic ? options.iconicBooks : options.skillBooks;
        if (select.options[0]) select.options[0].text = iconic ? 'Book…' : 'Skill…';
        for (const row of pool) {
            const abbreviation = 'sourceAbbr' in row ? row.sourceAbbr : '';
            const name = decodeHtmlEntitiesForPlainText(row.name);
            const option = new Option(abbreviation ? `${name} (${abbreviation})` : name, row.id);
            option.dataset.icon = row.iconUrl ? `<img src="${escapeHtml(row.iconUrl)}" width="25" height="25" alt="">` : iconMap(row.id, 25, 25);
            select.add(option);
        }
        select.dataset.ftLootLoaded = '1'; select.classList.add('ts-select');
    });
    try { window.initializeTomSelect?.(); } catch { /* The populated native select remains usable. */ }
}
function optionReadFailed(root: Element | Document): void {
    root.querySelectorAll<HTMLSelectElement>('select[data-ft-loot-pool]:not([data-ft-loot-loaded])').forEach(select => {
        if (select.options[0]) select.options[0].text = 'Unable to load — focus to retry';
    });
}
// The first book-tab opening starts the read; focus is also a retry path.
document.addEventListener('shown.bs.tab', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const panel = target.closest('#div_encounter_random_generators'); if (!panel) return;
    void randomGeneratorOptions().then(options => populateRandomGeneratorOptions(panel, options)).catch(() => optionReadFailed(panel));
});
document.addEventListener('focusin', event => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.dataset.ftLootPool || select.dataset.ftLootLoaded) return;
    const parent = select.parentElement; if (!parent) return;
    void randomGeneratorOptions().then(options => { if (select.isConnected) populateRandomGeneratorOptions(parent, options); }).catch(() => optionReadFailed(parent));
});
